import {plural, t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {semanticColor, sizing} from "@khanacademy/wonder-blocks-tokens";
import {
    Body,
    HeadingSmall,
    LabelSmall,
} from "@khanacademy/wonder-blocks-typography";

import EmailSubscriptionSwitch from "./email-subscription-switch.tsx";
import type {EmailSubscriptions} from "./types.ts";

const strings = {
    separateEmails: (numClasses: number) =>
        plural(numClasses, {
            one: "Receive a separate email for this class:",
            other: "Receive separate emails for these classes:",
        }),
} as const;

interface Props {
    hasStudents: boolean;
    allStudentsList: EmailSubscriptions["allStudentsCoachReportEmailSubscription"];
    studentLists: EmailSubscriptions["studentListEmailSubscriptions"];
    hasChildren: boolean;
    childrenList: EmailSubscriptions["parentReportEmailSubscriptions"];
}

/**
 * A component to manage email settings for activity reports.
 */
export default function EmailReportsSection({
    hasStudents,
    allStudentsList,
    studentLists,
    hasChildren,
    childrenList,
}: Props): React.ReactElement {
    const [showError, setShowError] = React.useState(false);

    const children = (
        <View style={{gap: sizing.size_120}}>
            <Body>
                {t`Receive a summary of what your children have been working on:`}
            </Body>

            {(childrenList ?? []).map((child) => (
                <React.Fragment key={child.key}>
                    <EmailSubscriptionSwitch
                        checked={child.checked}
                        label={child.description}
                        subscriptionKey={child.key}
                        showError={() => setShowError(true)}
                        clearError={() => setShowError(false)}
                        inputId={"childNotification" + child.key}
                    />
                </React.Fragment>
            ))}
        </View>
    );

    const students = (
        <View style={{gap: sizing.size_120}}>
            <Body>
                {t`Receive a summary of what your students have been working on:`}
            </Body>

            <EmailSubscriptionSwitch
                checked={!!allStudentsList?.checked}
                label={t`One email with all your students`}
                subscriptionKey={allStudentsList?.key}
                showError={() => setShowError(true)}
                clearError={() => setShowError(false)}
                inputId="allStudentsNotification"
            />

            {studentLists && studentLists.length > 0 && (
                <View style={{gap: sizing.size_120}}>
                    <Body>{strings.separateEmails(studentLists.length)}</Body>

                    {studentLists.map(
                        (student) =>
                            student && (
                                <View
                                    key={student.key}
                                    style={{gap: sizing.size_080}}
                                >
                                    <EmailSubscriptionSwitch
                                        checked={student.checked}
                                        label={student.description}
                                        subscriptionKey={student.key}
                                        showError={() => setShowError(true)}
                                        clearError={() => setShowError(false)}
                                        inputId={
                                            "studentNotification" + student.key
                                        }
                                    />
                                </View>
                            ),
                    )}
                </View>
            )}
        </View>
    );

    return (
        <View
            style={{gap: sizing.size_160}}
            role="region"
            aria-label={t`Weekly activity reports settings`}
        >
            <HeadingSmall tag="h2">{t`Weekly activity reports`}</HeadingSmall>
            {hasChildren && children}
            {hasStudents && students}
            {showError && (
                <LabelSmall style={styles.errorText}>
                    {t`Oops! Something went wrong. Please try again.`}
                </LabelSmall>
            )}
        </View>
    );
}

const styles = StyleSheet.create({
    errorText: {
        color: semanticColor.status.critical.foreground,
        marginBlockStart: sizing.size_040,
    },
});
